/**
 * Legacy disposition axes → Judgment Selling traits.
 * Runtime still stores grit/measure/selfProtect/bond + instinct;
 * judgmentOf() is the sell/new-module entry.
 */
import type { RaidDisposition } from "../../../data/raid-catalog";
import {
  clampTrait01to100,
  type JudgmentTraits,
} from "./traits";

export type LegacyDispositionRow = RaidDisposition & { instinct?: number };

/**
 * Fold measure + selfProtect → insight (equal weight).
 * grit demoted; bond/instinct pass-through.
 */
export function legacyToJudgment(row: LegacyDispositionRow): JudgmentTraits {
  const measure = clampTrait01to100(row.measure ?? 50);
  const selfProtect = clampTrait01to100(row.selfProtect ?? 50);
  return {
    instinct: clampTrait01to100(row.instinct ?? 50),
    insight: clampTrait01to100(0.5 * measure + 0.5 * selfProtect),
    bond: clampTrait01to100(row.bond ?? 50),
    grit: clampTrait01to100(row.grit ?? 50),
  };
}

/**
 * Soft-playable P0: one 통찰 predicts stand + AA pad (Sim Lab style).
 * measure = selfProtect = insight fold. Call after authored/absolute resolve.
 */
export function symmetrizeInsightAxes(
  d: RaidDisposition,
): RaidDisposition {
  const insight = clampTrait01to100(
    0.5 * (d.measure ?? 50) + 0.5 * (d.selfProtect ?? 50),
  );
  return { ...d, measure: insight, selfProtect: insight };
}

/**
 * Expand judgment → legacy knobs for existing mapping tables.
 * insight drives both measure and selfProtect so Soft≪AA pads stay coherent.
 */
export function judgmentToLegacy(
  j: JudgmentTraits,
): RaidDisposition & { instinct: number } {
  const insight = clampTrait01to100(j.insight);
  return {
    grit: clampTrait01to100(j.grit),
    measure: insight,
    selfProtect: insight,
    bond: clampTrait01to100(j.bond),
    instinct: clampTrait01to100(j.instinct),
  };
}
